/**
 * Honest data-gap detection for trend charts.
 *
 * Trend series (see `chart-data.ts`) OMIT missing time buckets rather than
 * carrying nulls, so Recharts draws a straight line across an outage as if the
 * feed never stopped. This module finds those gaps and produces:
 *   1. the synthetic null-marker rows that force the line to break, and
 *   2. the spans to shade with a warning band + a "stale since" label,
 * so a stopped feed reads as stopped instead of as a confident straight line.
 *
 * Both trend widgets and the building TrendView share this one implementation.
 */

import { getGapWindows } from "@/lib/api/agent";

/**
 * Defensive fallback for the minutes a channel may go silent before its line is treated as
 * stale. `GET /kb/gap-windows` (story #246) is the canonical KB-sourced source; this table is
 * used before that fetch resolves, if it fails, and for analytics-derived channels
 * (`energy_kwh`, `occupancy`, `ventilation_rate`) that have no backing KB sensor and so are
 * never returned by the endpoint. Do NOT grow smart behaviour here — extend the KB instead.
 */
const GAP_THRESHOLD_MINUTES: Readonly<Record<string, number>> = {
  indoor_temperature: 15,
  co2_ppm: 30,
  electricity_kwh: 60,
  heat_kwh: 60,
  water_m3: 60,
  // Analytics-derived series inherit the hourly cadence of their source.
  energy_kwh: 60,
  occupancy: 60,
  ventilation_rate: 60,
};

const DEFAULT_GAP_THRESHOLD_MINUTES = 60;

// KB-sourced gap windows, fetched once and cached. `gapThresholdMinutes` must stay synchronous
// (it runs inline during chart render), so the fetch happens in the background and callers read
// whatever is cached so far, falling back to the interim table above until it resolves.
let kbGapWindows: Record<string, number> | null = null;
let kbGapWindowsFetch: Promise<void> | null = null;

function ensureKBGapWindowsFetched(): void {
  if (kbGapWindowsFetch) return;
  kbGapWindowsFetch = getGapWindows()
    .then((windows) => {
      kbGapWindows = windows;
    })
    .catch(() => {
      // Fetch failed — keep reading the interim fallback table above.
    });
}

/** The single seam for per-channel staleness. Unknown channels fall back to the default. */
export function gapThresholdMinutes(channel: string): number {
  ensureKBGapWindowsFetched();
  return (
    kbGapWindows?.[channel] ??
    GAP_THRESHOLD_MINUTES[channel] ??
    DEFAULT_GAP_THRESHOLD_MINUTES
  );
}

const MINUTE_MS = 60_000;

/**
 * Tolerance over the series' own typical spacing. `sample_rows()` decimates
 * long windows, so the normal gap between rows can exceed a raw per-channel
 * threshold even with complete data. A delta is only a real outage when it is
 * also large relative to the series' cadence — this factor is the "also large"
 * margin, keeping normally-sparse channels from fragmenting.
 */
const CADENCE_TOLERANCE = 1.5;

export interface TimeGap {
  /** ts of the last present row before the gap (a real value on the x-axis). */
  from: string;
  /** ts of the first present row after the gap (a real value on the x-axis). */
  to: string;
  /** ISO ts of the synthetic null-marker row placed inside the gap to break the line. */
  marker: string;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Find spans where the feed went silent longer than it should have.
 *
 * The strictest plotted channel decides staleness (its threshold is the floor);
 * that floor is then raised to `CADENCE_TOLERANCE x` the series' median spacing
 * so downsampled-but-complete data is not mistaken for an outage.
 */
export function detectGaps<T>(
  rows: readonly T[],
  thresholdChannels: readonly string[],
  getTs: (row: T) => string,
): TimeGap[] {
  if (rows.length < 2 || thresholdChannels.length === 0) return [];

  const times = rows.map((r) => Date.parse(getTs(r)));
  const deltas: number[] = [];
  for (let i = 1; i < times.length; i++) deltas.push(times[i] - times[i - 1]);

  const floorMs =
    Math.min(...thresholdChannels.map(gapThresholdMinutes)) * MINUTE_MS;
  const effectiveMs = Math.max(floorMs, median(deltas) * CADENCE_TOLERANCE);

  const gaps: TimeGap[] = [];
  for (let i = 1; i < rows.length; i++) {
    if (times[i] - times[i - 1] > effectiveMs) {
      gaps.push({
        from: getTs(rows[i - 1]),
        to: getTs(rows[i]),
        marker: new Date((times[i - 1] + times[i]) / 2).toISOString(),
      });
    }
  }
  return gaps;
}

/**
 * Splice a null-valued marker row into each gap so Recharts (with `connectNulls`
 * off) breaks the line there. The caller supplies `makeMarker` because each
 * widget has its own row shape and timestamp key; every plotted channel in the
 * marker row must be `null`. Rows are returned re-sorted by timestamp.
 */
export function withGapMarkers<T>(
  rows: readonly T[],
  gaps: readonly TimeGap[],
  makeMarker: (markerTs: string) => T,
  getTs: (row: T) => string,
): T[] {
  if (gaps.length === 0) return [...rows];
  const merged = [...rows, ...gaps.map((g) => makeMarker(g.marker))];
  return merged.sort((a, b) => getTs(a).localeCompare(getTs(b)));
}

/** Recharts gap-band fill — the theme warning token, never a raw color. */
export const GAP_FILL = "var(--warning)";
export const GAP_FILL_OPACITY = 0.12;

/** Localized "stale since <ts>" label, formatted with the chart's own ts formatter. */
export function staleSinceLabel(
  fromTs: string,
  fmt: (iso: string) => string,
): string {
  return `Vanhentunut alkaen ${fmt(fromTs)}`;
}
